import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, Loader2, GripVertical, Image as ImageIcon, Save, Send, X, ArrowUp, ArrowDown,
} from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DIFFS = [{ v: "facil", l: "Fácil" }, { v: "medio", l: "Médio" }, { v: "dificil", l: "Difícil" }];

export default function PublishProject() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);

  const [form, setForm] = useState({
    title: "", description: "", category: "", subcategory: "", difficulty: "facil",
    width: "", height: "", depth: "", unit: "cm", build_time: "", cost: "", pallets_count: "",
    tips: "", notes: "",
  });
  const [materials, setMaterials] = useState([{ name: "", quantity: "", note: "" }]);
  const [tools, setTools] = useState([]);
  const [toolInput, setToolInput] = useState("");
  const [images, setImages] = useState([]); // [{url}]
  const [steps, setSteps] = useState([{ title: "", description: "", images: [] }]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/projects/${editId}`).then((r) => {
      const p = r.data;
      setForm({
        title: p.title, description: p.description || "", category: p.category, subcategory: p.subcategory || "",
        difficulty: p.difficulty, width: p.width ?? "", height: p.height ?? "", depth: p.depth ?? "",
        unit: p.unit || "cm", build_time: p.build_time || "", cost: p.cost ?? "", pallets_count: p.pallets_count ?? "",
        tips: p.tips || "", notes: p.notes || "",
      });
      setMaterials(p.materials?.length ? p.materials : [{ name: "", quantity: "", note: "" }]);
      setTools(p.tools || []);
      setImages(p.images?.length ? p.images.map((i) => ({ url: i.url })) : []);
      setSteps(p.steps?.length ? p.steps.map((s) => ({ title: s.title, description: s.description, images: s.images || [] })) : [{ title: "", description: "", images: [] }]);
    }).catch(() => toast.error("Projeto não encontrado")).finally(() => setLoading(false));
  }, [editId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildPayload = (status) => ({
    ...form,
    width: form.width === "" ? null : parseFloat(form.width),
    height: form.height === "" ? null : parseFloat(form.height),
    depth: form.depth === "" ? null : parseFloat(form.depth),
    cost: form.cost === "" ? 0 : parseFloat(form.cost),
    pallets_count: form.pallets_count === "" ? 0 : parseInt(form.pallets_count, 10),
    materials: materials.filter((m) => m.name.trim()).map((m) => ({ ...m, price: parseFloat(String(m.price ?? "").replace(",", ".")) || 0 })),
    tools,
    images: images.map((im, i) => ({ url: im.url, order: i })),
    steps: steps.filter((s) => s.title.trim()).map((s, i) => ({ number: i + 1, title: s.title, description: s.description, images: s.images })),
    status,
  });

  const validate = () => {
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error("Informe um título (mín. 3 caracteres)"); return false; }
    if (!form.category) { toast.error("Selecione uma categoria"); return false; }
    return true;
  };

  const submit = async (status) => {
    if (status === "published" && !validate()) return;
    if (status === "draft" && !form.title.trim()) { toast.error("Informe ao menos um título para salvar"); return; }
    setSaving(true);
    try {
      const payload = buildPayload(status);
      const res = editId ? await api.put(`/projects/${editId}`, payload) : await api.post("/projects", payload);
      toast.success(status === "draft" ? "Rascunho salvo!" : "Projeto publicado com sucesso!");
      if (status === "published") navigate(`/projeto/${res.data.slug}`);
      else navigate("/painel");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-8">
        <p className="eyebrow text-primary">{editId ? "Editando projeto" : "Novo projeto"}</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">{editId ? "Editar projeto" : "Publicar novo projeto"}</h1>
        <p className="mt-2 text-muted-foreground">Compartilhe cada detalhe para outros makers reproduzirem.</p>
      </div>

      <div className="space-y-8">
        <SectionCard title="Informações básicas" n={1}>
          <FieldRow>
            <Field label="Título *"><Input data-testid="publish-title" value={form.title} onChange={set("title")} placeholder="Ex: Criado-mudo de palete com gaveta" /></Field>
          </FieldRow>
          <Field label="Descrição">
            <Textarea data-testid="publish-description" value={form.description} onChange={set("description")} placeholder="Descreva o projeto..." className="min-h-[100px] rounded-xl" />
          </Field>
          <FieldRow cols={2}>
            <Field label="Categoria *">
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="publish-category" className="rounded-xl"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Subcategoria"><Input data-testid="publish-subcategory" value={form.subcategory} onChange={set("subcategory")} placeholder="Ex: Com gaveta" /></Field>
          </FieldRow>
          <Field label="Nível de dificuldade">
            <div className="flex gap-2">
              {DIFFS.map((d) => (
                <button key={d.v} type="button" data-testid={`difficulty-${d.v}`} onClick={() => setForm((f) => ({ ...f, difficulty: d.v }))}
                  className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${form.difficulty === d.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}>
                  {d.l}
                </button>
              ))}
            </div>
          </Field>
        </SectionCard>

        <SectionCard title="Informações do projeto" n={2}>
          <FieldRow cols={4}>
            <Field label="Largura"><Input data-testid="publish-width" type="number" value={form.width} onChange={set("width")} placeholder="0" /></Field>
            <Field label="Altura"><Input data-testid="publish-height" type="number" value={form.height} onChange={set("height")} placeholder="0" /></Field>
            <Field label="Profund."><Input data-testid="publish-depth" type="number" value={form.depth} onChange={set("depth")} placeholder="0" /></Field>
            <Field label="Unidade">
              <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="m">m</SelectItem><SelectItem value="mm">mm</SelectItem></SelectContent>
              </Select>
            </Field>
          </FieldRow>
          <FieldRow cols={3}>
            <Field label="Tempo de construção"><Input data-testid="publish-time" value={form.build_time} onChange={set("build_time")} placeholder="Ex: 1 dia" /></Field>
            <Field label="Custo aprox. (R$)"><Input data-testid="publish-cost" type="number" value={form.cost} onChange={set("cost")} placeholder="0,00" /></Field>
            <Field label="Nº de paletes"><Input data-testid="publish-pallets" type="number" value={form.pallets_count} onChange={set("pallets_count")} placeholder="0" /></Field>
          </FieldRow>
        </SectionCard>

        <SectionCard title="Materiais" n={3}>
          <div className="space-y-3">
            {materials.map((m, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2">
                <Input data-testid={`material-name-${i}`} value={m.name} onChange={(e) => setMaterials((arr) => arr.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Material" className="flex-1" />
                <Input data-testid={`material-qty-${i}`} value={m.quantity} onChange={(e) => setMaterials((arr) => arr.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} placeholder="Qtd" className="sm:w-28" />
                <Input data-testid={`material-price-${i}`} inputMode="decimal" value={m.price ?? ""} onChange={(e) => setMaterials((arr) => arr.map((x, idx) => idx === i ? { ...x, price: e.target.value.replace(/[^\d.,]/g, "") } : x))} placeholder="Preço R$" className="sm:w-28" />
                <Input value={m.note} onChange={(e) => setMaterials((arr) => arr.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))} placeholder="Observação" className="flex-1" />
                <Button type="button" variant="ghost" size="icon" onClick={() => setMaterials((arr) => arr.filter((_, idx) => idx !== i))} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <Button type="button" data-testid="add-material-btn" variant="outline" size="sm" onClick={() => setMaterials((arr) => [...arr, { name: "", quantity: "", note: "" }])} className="mt-3 rounded-full gap-1.5"><Plus className="h-4 w-4" /> Adicionar material</Button>
        </SectionCard>

        <SectionCard title="Ferramentas" n={4}>
          <div className="flex gap-2">
            <Input data-testid="tool-input" value={toolInput} onChange={(e) => setToolInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (toolInput.trim()) { setTools((t) => [...t, toolInput.trim()]); setToolInput(""); } } }}
              placeholder="Ex: Furadeira (Enter para adicionar)" className="rounded-xl" />
            <Button type="button" data-testid="add-tool-btn" onClick={() => { if (toolInput.trim()) { setTools((t) => [...t, toolInput.trim()]); setToolInput(""); } }} className="rounded-full">Adicionar</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {tools.map((t, i) => (
              <span key={i} className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm">
                {t}<button type="button" onClick={() => setTools((arr) => arr.filter((_, idx) => idx !== i))}><X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" /></button>
              </span>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Fotos" n={5}>
          <p className="mb-3 text-sm text-muted-foreground">A primeira imagem será usada como capa. Use as setas para reordenar.</p>
          <MultiImageUploader images={images} setImages={setImages} />
        </SectionCard>

        <SectionCard title="Passo a passo" n={6}>
          <div className="space-y-5">
            {steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-secondary/30 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2 font-semibold"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm text-primary-foreground">{i + 1}</span> Etapa {i + 1}</span>
                  {steps.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setSteps((arr) => arr.filter((_, idx) => idx !== i))} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>}
                </div>
                <Input data-testid={`step-title-${i}`} value={s.title} onChange={(e) => setSteps((arr) => arr.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} placeholder="Título da etapa" className="mb-2 rounded-lg bg-card" />
                <Textarea data-testid={`step-desc-${i}`} value={s.description} onChange={(e) => setSteps((arr) => arr.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} placeholder="Descreva o que fazer nesta etapa..." className="rounded-lg bg-card min-h-[70px]" />
                <div className="mt-2">
                  <StepImages images={s.images} onChange={(imgs) => setSteps((arr) => arr.map((x, idx) => idx === i ? { ...x, images: imgs } : x))} />
                </div>
              </div>
            ))}
          </div>
          <Button type="button" data-testid="add-step-btn" variant="outline" size="sm" onClick={() => setSteps((arr) => [...arr, { title: "", description: "", images: [] }])} className="mt-3 rounded-full gap-1.5"><Plus className="h-4 w-4" /> Adicionar etapa</Button>
        </SectionCard>

        <SectionCard title="Dicas e observações" n={7}>
          <Field label="Dicas importantes"><Textarea data-testid="publish-tips" value={form.tips} onChange={set("tips")} placeholder="Dicas para quem vai reproduzir..." className="rounded-xl min-h-[80px]" /></Field>
          <Field label="Observações"><Textarea data-testid="publish-notes" value={form.notes} onChange={set("notes")} placeholder="Observações livres..." className="rounded-xl min-h-[70px]" /></Field>
        </SectionCard>

        <div className="sticky bottom-20 lg:bottom-4 flex gap-3 rounded-2xl border border-border bg-card/95 glass p-4 shadow-lg">
          <Button data-testid="save-draft-btn" variant="outline" disabled={saving} onClick={() => submit("draft")} className="flex-1 rounded-full gap-1.5">
            <Save className="h-4 w-4" /> Salvar rascunho
          </Button>
          <Button data-testid="publish-submit-btn" disabled={saving} onClick={() => submit("published")} className="flex-1 rounded-full gap-1.5 font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publicar projeto
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, n, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary text-sm text-primary font-mono">{n}</span>{title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="text-sm font-medium">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
function FieldRow({ children, cols = 1 }) {
  const grid = { 1: "", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4" }[cols];
  return <div className={`grid gap-4 ${grid}`}>{children}</div>;
}

function useUploader() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha no upload");
      return null;
    } finally { setUploading(false); }
  };
  return { uploading, upload };
}

function MultiImageUploader({ images, setImages }) {
  const inputRef = useRef();
  const { uploading, upload } = useUploader();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const url = await upload(f);
      if (url) setImages((arr) => [...arr, { url }]);
    }
    e.target.value = "";
  };

  const move = (i, dir) => setImages((arr) => {
    const next = [...arr]; const j = i + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[i], next[j]] = [next[j], next[i]]; return next;
  });

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((im, i) => (
          <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-border">
            <img src={mediaUrl(im.url)} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute left-1.5 top-1.5 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">Capa</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 p-1 opacity-0 transition group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} className="text-white"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="text-white"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" data-testid={`remove-image-${i}`} onClick={() => setImages((arr) => arr.filter((_, idx) => idx !== i))} className="text-white"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        <button type="button" data-testid="upload-image-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border text-muted-foreground transition hover:border-primary hover:text-primary">
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Upload className="h-6 w-6" /><span className="text-xs">Enviar foto</span></>}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
    </div>
  );
}

function StepImages({ images, onChange }) {
  const inputRef = useRef();
  const { uploading, upload } = useUploader();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) { const url = await upload(f); if (url) onChange([...images, url]); }
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2">
      {images.map((img, i) => (
        <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-border">
          <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
        </div>
      ))}
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
    </div>
  );
}
